import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Compass, Home, Gauge, Target, Route, FileText, Pause, Play } from 'lucide-react';

const ADVANCE_MS = 6500;

const SHOTS = [
  {
    id: 'together',
    label: 'One connected picture',
    icon: Compass,
    tint: 'glass-cyan',
    accent: 'text-cyan-300',
    bar: 'bg-cyan-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(34,211,238,0.55)]',
    title: 'Everything measured against one number',
    body: 'Your readiness score, the cost of the life you want, and the next action on your plan, on one screen, all drawn from the same profile. Nothing here was typed twice.',
    img: '/screens/shot_dashboard.jpeg',
    imgClass: 'object-top',
    stats: [['Built from your own inputs', 'Assessment, income floor and target career, named openly'], ['One next action', 'Not a list of forty things, the one that moves the score']],
  },
  {
    id: 'lifestyle',
    label: 'Lifestyle Design',
    icon: Home,
    tint: 'glass-violet',
    accent: 'text-violet-300',
    bar: 'bg-violet-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(167,139,250,0.55)]',
    title: 'Price the life first, then find work that funds it',
    body: 'Ten steps: where you will live, your home, getting around, health, the people who depend on you, what you owe, paying your future self, and what you actually want from the work. Skip anything that is not you.',
    img: '/screens/shot_lifestyle_step.jpeg',
    imgClass: 'object-top',
    stats: [['$86,309 a year', 'The salary this particular life needs, before tax'], ['Stop at any step', 'It calculates from what you have and says how firm it is']],
  },
  {
    id: 'honest',
    label: 'The honest part',
    icon: Gauge,
    tint: 'glass-emerald',
    accent: 'text-emerald-300',
    bar: 'bg-emerald-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(52,211,153,0.5)]',
    title: 'It tells you what you have not accounted for',
    body: 'The deductible you would have to cover. Car upkeep. Subscriptions. Then the levers that close the gap, priced from your own answers, and where what you say you want disagrees with what your assessment measured.',
    img: '/screens/shot_lifestyle_results.jpeg',
    imgClass: 'object-top',
    stats: [['Judged on both tests', 'Does it clear your floor, and does the work suit you'], ['"Worth a second look"', 'It will disagree with you when the data does']],
  },
  {
    id: 'readiness',
    label: 'Career Launch Readiness',
    icon: Target,
    tint: 'glass-amber',
    accent: 'text-amber-300',
    bar: 'bg-amber-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(251,191,36,0.5)]',
    title: 'One number that moves as you do',
    body: 'Measured against your own target career, not a generic average: academics, skills, experience, professional presence, network and job search, each with the single next action that moves it most.',
    img: '/screens/shot_readiness.jpeg',
    imgClass: 'object-top',
    stats: [['Six components', 'You can see exactly which one is holding you back'], ['Measured against your target', 'Change the target, the score re-reads']],
  },
  {
    id: 'futureself',
    label: 'Paths to your future self',
    icon: Route,
    tint: 'glass-violet',
    accent: 'text-violet-300',
    bar: 'bg-violet-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(167,139,250,0.5)]',
    title: 'Four versions of your future, priced',
    body: 'Stay put, get there funded, get there borrowed, or earn your way in. Each one shows income at your chosen horizon, debt carried, AI exposure, the tradeoffs and what would have to be true.',
    img: '/screens/shot_futureself.jpeg',
    imgClass: 'object-top',
    stats: [['$158,405 by 2031', 'Funded route, no debt carried'], ['What has to be true', 'The assumptions stated, not buried']],
  },
  {
    id: 'report',
    label: 'Your report',
    icon: FileText,
    tint: 'glass-cyan',
    accent: 'text-cyan-300',
    bar: 'bg-cyan-300',
    ring: 'shadow-[0_0_80px_-12px_rgba(34,211,238,0.5)]',
    title: 'Nineteen sections, every word from your own data',
    body: 'Assessment, life design against career fit, the cost of the life you want, readiness, brand and network, learning ROI, future paths, funding, goals and a dated action plan, assembled and downloadable.',
    img: '/screens/shot_report.jpeg',
    imgClass: 'object-top',
    stats: [['19 sections', 'Choose all of it or only what you need'], ['Share it', 'Counselors, advisors and parents read the same thing you do']],
  },
];

/* Uniform 16:10 frame, no browser chrome, no URL bar, no account names. */
const AppFrame = ({ shot }) => (
  <div className={`rounded-2xl overflow-hidden border border-white/12 bg-[#0C2B49] ${shot.ring}`}>
    <div className="relative aspect-[16/10] overflow-hidden">
      <img src={shot.img} alt={shot.title} loading="lazy"
        className={`absolute inset-0 h-full w-full object-cover ${shot.imgClass}`} />
    </div>
  </div>
);

export default function ProductShowcase() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % SHOTS.length), ADVANCE_MS);
    return () => clearTimeout(timer.current);
  }, [index, playing]);

  const shot = SHOTS[index];
  const select = (i) => { setIndex(i); setPlaying(false); };

  return (
    <section id="inside" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="product-showcase">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300">Inside the platform</span>
          <h2 className="font-outfit text-4xl sm:text-5xl font-bold text-white mt-4">
            This is what the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-cyan-300 to-violet-300">intelligence looks like</span>
          </h2>
          <p className="text-base md:text-lg text-slate-200/85 mt-5">
            Real screens from the live platform. Every number below was calculated from one person's profile,             and every screen reads from the same profile, whether they are 16 and choosing courses or 43 and rebuilding.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2.5 mt-10" data-testid="showcase-tabs">
          {SHOTS.map((s, i) => (
            <button key={s.id} onClick={() => select(i)} data-testid={`showcase-tab-${s.id}`}
              className={`relative overflow-hidden inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-colors duration-200 ${
                index === i
                  ? 'border-cyan-300/60 bg-cyan-300/15 text-white'
                  : 'border-white/12 bg-white/[0.05] text-slate-300 hover:text-white hover:border-white/30'
              }`}>
              <s.icon className={`w-4 h-4 ${index === i ? s.accent : ''}`} /> {s.label}
              {index === i && playing && (
                <motion.span key={`p-${index}`} className={`absolute bottom-0 left-0 h-[2px] ${s.bar}`}
                  initial={{ width: '0%' }} animate={{ width: '100%' }}
                  transition={{ duration: ADVANCE_MS / 1000, ease: 'linear' }} />
              )}
            </button>
          ))}
          <button onClick={() => setPlaying((p) => !p)} data-testid="showcase-playpause"
            aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
            className="ml-1 inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/12 bg-white/[0.05] text-slate-300 hover:text-white hover:border-white/30 transition-colors duration-200">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={shot.id} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.2, 0.7, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center mt-12">
            <div className={`lg:col-span-2 glass-tint ${shot.tint} p-8`}>
              <shot.icon className={`w-7 h-7 ${shot.accent}`} />
              <h3 className="font-outfit text-2xl sm:text-3xl font-semibold text-white mt-5">{shot.title}</h3>
              <p className="text-base text-slate-200/85 mt-4 leading-relaxed">{shot.body}</p>
              <div className="mt-7 space-y-4">
                {shot.stats.map(([big, small]) => (
                  <div key={big} className="border-l-2 border-white/20 pl-4">
                    <p className={`font-outfit text-lg font-semibold ${shot.accent}`}>{big}</p>
                    <p className="text-sm text-slate-300/80">{small}</p>
                  </div>
                ))}
              </div>
              <Link to="/pricing" data-testid={`showcase-cta-${shot.id}`}
                className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-white hover:gap-3 transition-all duration-200">
                Start building yours <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-3">
              <AppFrame shot={shot} />
            </div>
          </motion.div>
        </AnimatePresence>

        <p className="text-center text-xs text-slate-300/70 mt-8">
          Screens captured from the live platform. Names and account details shown are demonstration data.
        </p>
      </div>
    </section>
  );
}
